import { LOCALSTORAGE_TEAM_SELECTED_KEY } from "@/contexts/teams-provider";
import { getBearerToken, useApi } from "@/hooks/useApi";
import { useWakeLock } from "@/hooks/useWakeLock";
import {
  MicrophoneError,
  startPcmRecorder,
  type PcmRecorder,
} from "@/lib/audio/pcm-recorder";
import { pcmToWavBlob } from "@/lib/audio/wav";
import { delay, isOffline, waitForConnection } from "@/lib/network";
import {
  TRANSCRIPTION_MAX_DURATION_MS,
  TRANSCRIPTION_SAMPLE_RATE,
  type TranscriptionClientMessage,
  type TranscriptionResult,
  type TranscriptionServerMessage,
} from "@shaarp/common";
import axios, { type AxiosInstance } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

const WS_PATH = "/ws/transcription";
const BATCH_PATH = "/transcription";

/**
 * How long to wait for the socket to become usable before giving up on the
 * realtime path. Beyond this the recording still runs and is salvaged by the
 * batch fallback, so the user never loses what they said.
 */
const CONNECT_TIMEOUT_MS = 8000;

/**
 * File extension matching a recording container. Cosmetic — the provider reads
 * the MIME type — but a mismatched name is misleading in logs and storage.
 */
const extensionFor = (mimeType: string): string => {
  if (mimeType.includes("mp4")) return "m4a";
  if (mimeType.includes("ogg")) return "ogg";
  return "webm";
};

/**
 * How long to wait for the final transcript after asking the upstream to stop.
 * Without it a silent server would strand the session until its own idle
 * timeout, leaving the user staring at a spinner.
 */
const FINALIZE_TIMEOUT_MS = 10000;

/**
 * How long a *fallback* upload keeps trying to reach the API before giving up.
 *
 * Here the recording exists nowhere but this tab's memory, so a single failed
 * request throws away everything the user just said. A phone that walked out of
 * Wi-Fi range takes a few seconds to hand over to cellular, and a lift or a
 * tunnel a good deal longer; two minutes covers both without leaving anyone in
 * front of a spinner indefinitely.
 */
const FALLBACK_UPLOAD_DEADLINE_MS = 120000;

/**
 * How long a *refinement* upload is given.
 *
 * A refinement only upgrades text the user can already read, so a bad
 * connection must not hold the composer hostage for it: one quick retry, then
 * the realtime transcript stands as the final answer.
 */
const REFINE_UPLOAD_DEADLINE_MS = 8000;

/** Base pause between two upload attempts, multiplied by the attempt number. */
const UPLOAD_RETRY_DELAY_MS = 2000;

/**
 * Whether a failed upload is worth retrying.
 *
 * A request that never reached the server carries no response at all — DNS
 * failure, dropped socket, airplane mode — and will go through once the
 * connection is back. A 5xx or a 429 is transient server-side. Anything else
 * (400 on a malformed recording, 401 on an expired session) fails identically
 * however many times it is sent.
 */
const isRetriableUploadError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) {
    return false;
  }
  const status = error.response?.status;
  return status === undefined || status === 429 || status >= 500;
};

/** The recording as it is handed to the batch endpoint. */
type RecordingUpload = {
  blob: Blob;
  filename: string;
  /** ISO-639-1 hint, when the caller knows the language. */
  language?: string;
};

/**
 * POST the recording once. A fresh `FormData` is built per call so a retry
 * never resends a body the previous attempt already consumed.
 */
const postRecording = async (
  api: AxiosInstance,
  upload: RecordingUpload,
): Promise<TranscriptionResult> => {
  const body = new FormData();
  body.append("audio", upload.blob, upload.filename);
  if (upload.language) {
    body.append("language", upload.language);
  }
  const { data } = await api.post<TranscriptionResult>(BATCH_PATH, body);
  return data;
};

/**
 * Upload the recording, riding out a connection that comes and goes.
 *
 * Attempts continue until `deadlineMs` elapses. Between them the loop waits on
 * the browser's `online` event rather than burning retries against an interface
 * with no route out, so a dictation recorded in a lift is sent the moment the
 * network returns instead of being discarded.
 *
 * @param deadlineMs - Total time the upload may spend trying.
 * @param onWaiting - Called before the first wait, so the caller can tell the
 *   user their audio is held rather than lost.
 * @param isAbandoned - Polled between attempts; stops the loop when nobody is
 *   left to receive the transcript.
 */
const uploadRecording = async (
  api: AxiosInstance,
  upload: RecordingUpload,
  { deadlineMs, onWaiting, isAbandoned }: {
    deadlineMs: number;
    onWaiting: () => void;
    isAbandoned: () => boolean;
  },
): Promise<TranscriptionResult> => {
  const deadline = Date.now() + deadlineMs;

  for (let attempt = 1; ; attempt += 1) {
    try {
      return await postRecording(api, upload);
    } catch (error) {
      const remaining = deadline - Date.now();
      if (!isRetriableUploadError(error) || remaining <= 0 || isAbandoned()) {
        throw error;
      }
      if (attempt === 1) {
        onWaiting();
      }
      await waitForConnection(remaining);
      const left = deadline - Date.now();
      if (left <= 0 || isAbandoned()) {
        throw error;
      }
      await delay(Math.min(UPLOAD_RETRY_DELAY_MS * attempt, left));
    }
  }
};

export type VoiceTranscriptionStatus =
  /** Nothing running. */
  | "idle"
  /** Microphone and socket are being acquired. */
  | "starting"
  /** Capturing and streaming. */
  | "listening"
  /** Capture stopped; waiting on the final transcript. */
  | "finalizing";

export type UseVoiceTranscriptionOptions = {
  /**
   * Receives the full transcript of the current session every time it grows.
   * `isFinal` marks the settled result, after which no further call is made.
   */
  onTranscript: (text: string, isFinal: boolean) => void;
  /** The user denied microphone access. */
  onPermissionDenied?: () => void;
  /** Transcription failed and no text could be recovered. */
  onError?: () => void;
  /**
   * The device lost its connection during the session. Called at most once, and
   * only when the recording survives it: the audio is held and sent as soon as
   * the network is back. Exists so the UI can say that, instead of showing a
   * spinner the user is tempted to reload away.
   */
  onConnectionLost?: () => void;
  /** ISO-639-1 hint for the batch fallback (the realtime model auto-detects). */
  language?: string;
};

export type UseVoiceTranscription = {
  status: VoiceTranscriptionStatus;
  isRecording: boolean;
  /** True while the final transcript is still being awaited. */
  isFinalizing: boolean;
  /** Live capture stream, for the visualizer. Null when not recording. */
  stream: MediaStream | null;
  /**
   * False once the realtime channel is gone. Recording continues, but the
   * transcript will only arrive when the user stops, so the UI must say so
   * instead of leaving a frozen half-sentence on screen.
   */
  isLiveTranscript: boolean;
  /**
   * True while the recording is held waiting for the connection to come back.
   * The audio is safe; it is sent as soon as there is a route out.
   */
  isAwaitingConnection: boolean;
  /** Begin a dictation session. Must be called from a user gesture. */
  start: () => Promise<void>;
  /** Stop capturing and keep the transcript. */
  stop: () => void;
  /** Stop capturing and discard the transcript. */
  cancel: () => void;
};

/** Derive the WebSocket origin from the configured HTTP API base. */
const getSocketUrl = (): string => {
  const base = import.meta.env.VITE_API_BASE_URL as string;
  const url = new URL(base, window.location.origin);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = `${url.pathname.replace(/\/+$/, "")}${WS_PATH}`;
  return url.toString();
};

/**
 * Live dictation backed by Voxtral realtime transcription.
 *
 * Audio is captured as PCM and streamed to the API, which proxies it to the
 * model and streams text back. The same PCM is retained in memory so that a
 * socket that never opens — or dies mid-sentence — falls back to uploading the
 * recording instead of losing it.
 */
export const useVoiceTranscription = (
  options: UseVoiceTranscriptionOptions,
): UseVoiceTranscription => {
  const [status, setStatus] = useState<VoiceTranscriptionStatus>("idle");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isLiveTranscript, setIsLiveTranscript] = useState(true);
  const [isAwaitingConnection, setIsAwaitingConnection] = useState(false);

  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const socketRef = useRef<WebSocket | null>(null);
  const recorderRef = useRef<PcmRecorder | null>(null);
  const durationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const connectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Every PCM chunk of the session, kept for the batch fallback. */
  const audioChunksRef = useRef<ArrayBuffer[]>([]);
  /**
   * The recorder that just stopped, held only long enough to read its
   * compressed copy for the batch upload.
   */
  const finishedRecorderRef = useRef<PcmRecorder | null>(null);
  /**
   * Resolves once the recorder has flushed and {@link finishedRecorderRef} holds
   * it. The compressed copy does not exist before then, so a batch upload that
   * skipped this wait would send the raw PCM instead.
   */
  const recorderFlushRef = useRef<Promise<void> | null>(null);
  /** Transcript deltas received so far. */
  const transcriptRef = useRef("");
  /** Set once the session ended, so a late event cannot re-open it. */
  const isSettledRef = useRef(false);
  /** Set by `cancel()`, so no transcript is emitted for a discarded session. */
  const isDiscardedRef = useRef(false);
  /** True once the socket is authenticated and accepting audio. */
  const isSocketReadyRef = useRef(false);
  /**
   * Set when the realtime path gave up. Capture keeps running; the session
   * finishes through the batch endpoint instead.
   */
  const isRealtimeDeadRef = useRef(false);
  /** Set once the connection loss has been reported, so it is announced once. */
  const isConnectionLossReportedRef = useRef(false);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Mirrors `status` for callbacks that must not be re-created when it changes:
  // the max-duration timer is scheduled inside `start`, so a `stop` closing over
  // the render-time status would still see "idle" and refuse to run.
  const statusRef = useRef<VoiceTranscriptionStatus>(status);
  const applyStatus = useCallback((next: VoiceTranscriptionStatus) => {
    statusRef.current = next;
    setStatus(next);
  }, []);

  /**
   * Cancel the connect deadline. It outlives neither a socket that opened nor
   * one that failed: left running, it would still be armed when the next session
   * starts and would fail over a channel that is perfectly healthy.
   */
  const clearConnectTimeout = useCallback(() => {
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
  }, []);

  /** Tear down every resource. Safe to call repeatedly. */
  const teardown = useCallback(() => {
    clearConnectTimeout();
    if (durationTimeoutRef.current) {
      clearTimeout(durationTimeoutRef.current);
      durationTimeoutRef.current = null;
    }
    if (finalizeTimeoutRef.current) {
      clearTimeout(finalizeTimeoutRef.current);
      finalizeTimeoutRef.current = null;
    }
    const socket = socketRef.current;
    socketRef.current = null;
    isSocketReadyRef.current = false;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      if (
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
      ) {
        socket.close();
      }
    }
    void recorderRef.current?.stop();
    recorderRef.current = null;
    setStream(null);
  }, [clearConnectTimeout]);

  const reset = useCallback(() => {
    teardown();
    audioChunksRef.current = [];
    finishedRecorderRef.current = null;
    recorderFlushRef.current = null;
    transcriptRef.current = "";
    setIsLiveTranscript(true);
    setIsAwaitingConnection(false);
    applyStatus("idle");
  }, [applyStatus, teardown]);

  /** Announce a lost connection once per session. */
  const reportConnectionLost = useCallback(() => {
    if (isConnectionLossReportedRef.current || isDiscardedRef.current) {
      return;
    }
    isConnectionLossReportedRef.current = true;
    optionsRef.current.onConnectionLost?.();
  }, []);

  /**
   * Transcribe the retained PCM through the batch model.
   *
   * Runs in two situations. As a *refinement* it upgrades a transcript the
   * realtime model already produced: measured on French dictation the batch
   * model scores 9.7% word error rate against realtime's 12.9%, and it is the
   * only one that accepts a vocabulary hint, so it spells product names right.
   * As a *fallback* it is the sole source of text, because the live channel
   * never opened or died mid-sentence.
   *
   * A refinement that fails is silent — the realtime text already on screen
   * stays. A fallback that fails has nothing to fall back to, so it reports.
   *
   * Either way the upload rides out a broken connection rather than discarding
   * the recording on the first failed request; see {@link uploadRecording}.
   */
  const runBatchTranscription = useCallback(
    async (mode: "refine" | "fallback") => {
      const chunks = audioChunksRef.current;
      if (isDiscardedRef.current || chunks.length === 0) {
        if (!isDiscardedRef.current && mode === "refine") {
          optionsRef.current.onTranscript(transcriptRef.current, true);
        }
        reset();
        return;
      }

      applyStatus("finalizing");
      teardown();

      try {
        // The compressed copy only becomes readable once the recorder has
        // flushed, so whichever path ended the session, wait for it here rather
        // than silently uploading the raw PCM.
        await recorderFlushRef.current?.catch(() => undefined);
        // Prefer the browser's compressed copy: same audio, roughly six times
        // smaller, which matters on mobile data where the PCM would be several
        // megabytes. Falls back to the PCM when no encoder was available.
        const compressed = finishedRecorderRef.current?.getCompressed() ?? null;
        // Goes through the shared client so auth, team and locale headers stay
        // consistent with every other call in the app.
        const data = await uploadRecording(
          apiRef.current,
          {
            blob:
              compressed?.blob ??
              pcmToWavBlob(chunks, TRANSCRIPTION_SAMPLE_RATE),
            filename: compressed
              ? `dictation.${extensionFor(compressed.mimeType)}`
              : "dictation.wav",
            language: optionsRef.current.language,
          },
          {
            deadlineMs:
              mode === "fallback"
                ? FALLBACK_UPLOAD_DEADLINE_MS
                : REFINE_UPLOAD_DEADLINE_MS,
            // Only a fallback is worth announcing: a refinement failing behind
            // a flaky connection changes nothing the user can see.
            onWaiting: () => {
              if (mode !== "fallback") return;
              setIsAwaitingConnection(true);
              reportConnectionLost();
            },
            isAbandoned: () => isDiscardedRef.current,
          },
        );

        if (data.text) {
          optionsRef.current.onTranscript(data.text, true);
        } else if (mode === "fallback") {
          optionsRef.current.onError?.();
        } else {
          // Refinement produced nothing: keep the realtime text but still mark
          // the session settled, or a caller waiting on `isFinal` would hang.
          optionsRef.current.onTranscript(transcriptRef.current, true);
        }
      } catch {
        if (mode === "fallback") {
          optionsRef.current.onError?.();
        } else {
          optionsRef.current.onTranscript(transcriptRef.current, true);
        }
      } finally {
        reset();
      }
    },
    [applyStatus, reportConnectionLost, reset, teardown],
  );

  /**
   * The realtime model delivered its transcript.
   *
   * It is shown straight away so the composer never waits, then handed to the
   * batch model for a second pass — same audio, a more accurate model, and the
   * product vocabulary applied. The refinement replaces the text a moment
   * later; if it fails, what is already on screen simply stays.
   */
  const settle = useCallback(
    (finalText: string) => {
      if (isSettledRef.current) return;
      isSettledRef.current = true;

      if (isDiscardedRef.current) {
        reset();
        return;
      }

      // Not final yet: the batch pass below owns the definitive text.
      optionsRef.current.onTranscript(finalText, false);
      void runBatchTranscription("refine");
    },
    [reset, runBatchTranscription],
  );

  /**
   * The realtime path failed. Capture is deliberately left running when the
   * user is still speaking — tearing the microphone down here would cut them
   * off mid-sentence, which is worse than losing the live preview. The audio
   * is already buffered, so `stop()` transcribes it through the batch endpoint
   * as if realtime had never been available.
   */
  const failOver = useCallback((reason: string) => {
    if (isSettledRef.current || isRealtimeDeadRef.current) return;
    isRealtimeDeadRef.current = true;
    clearConnectTimeout();

    // Without this the fallback is indistinguishable from a bug: the user sees
    // the composer stop filling in and nothing explains it.
    console.warn(
      `[dictation] live transcription unavailable (${reason}) — the transcript will arrive when you stop. Socket: ${getSocketUrl()}`,
    );

    const socket = socketRef.current;
    socketRef.current = null;
    isSocketReadyRef.current = false;
    if (socket) {
      socket.onopen = null;
      socket.onmessage = null;
      socket.onerror = null;
      socket.onclose = null;
      socket.close();
    }

    // Drop any partial live preview: the batch pass returns the whole
    // dictation, so keeping it would duplicate the opening words. The consumer
    // is told too — a half-sentence frozen on screen while the user keeps
    // talking reads as a crash.
    transcriptRef.current = "";
    if (!isDiscardedRef.current) {
      optionsRef.current.onTranscript("", false);
    }

    if (statusRef.current === "listening") {
      // Still capturing: the transcript now arrives only at the end.
      setIsLiveTranscript(false);
      return;
    }

    // Already stopping — nothing more will be captured, transcribe now.
    isSettledRef.current = true;
    const recorder = recorderRef.current;
    recorderRef.current = null;
    setStream(null);
    if (recorder) {
      recorderFlushRef.current = (async () => {
        await recorder.stop();
        finishedRecorderRef.current = recorder;
      })();
    }
    // With no recorder here, `stop()` already took it and owns the flush, so its
    // compressed copy is what the upload waits on. Starting a second, empty one
    // would send the raw PCM instead — several megabytes, on exactly the flaky
    // connection that brought us here.
    void runBatchTranscription("fallback");
  }, [clearConnectTimeout, runBatchTranscription]);

  const handleServerMessage = useCallback(
    (message: TranscriptionServerMessage) => {
      switch (message.type) {
        case "ready":
          isSocketReadyRef.current = true;
          clearConnectTimeout();
          break;
        case "transcript":
          // Full text, already vocabulary-corrected server-side — replace
          // rather than append, so a corrected word overwrites the old one.
          transcriptRef.current = message.text;
          if (!isDiscardedRef.current) {
            optionsRef.current.onTranscript(transcriptRef.current, false);
          }
          break;
        case "done":
          // The final frame carries the authoritative text, which may differ
          // from the concatenated deltas once the model revises its output.
          settle(message.text || transcriptRef.current);
          break;
        case "error":
          if (message.retriable) {
            failOver(`server: ${message.code}`);
          } else {
            isSettledRef.current = true;
            optionsRef.current.onError?.();
            reset();
          }
          break;
        case "language":
          break;
      }
    },
    [clearConnectTimeout, failOver, reset, settle],
  );

  const send = useCallback((message: TranscriptionClientMessage) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
    }
  }, []);

  const stop = useCallback(() => {
    if (isSettledRef.current || statusRef.current === "idle") {
      return;
    }
    applyStatus("finalizing");
    const recorder = recorderRef.current;
    recorderRef.current = null;
    setStream(null);

    // The recorder emits its final partial chunk while shutting down, so the
    // upstream must not be told to stop until that chunk has been forwarded —
    // otherwise the tail of the last word is dropped.
    const flushed = (async () => {
      await recorder?.stop();
      // Held so the batch pass can read its compressed copy.
      if (recorder) {
        finishedRecorderRef.current = recorder;
      }
    })();
    recorderFlushRef.current = flushed;

    void (async () => {
      await flushed;

      // `failOver` can run while the recorder is still flushing — a socket that
      // closes in that window — and it already owns the fallback from there. A
      // second pass would upload the same recording twice.
      if (isSettledRef.current) {
        return;
      }

      // Without a usable socket there is nothing to flush — transcribe the
      // buffered audio instead, so the recording still turns into text.
      if (isRealtimeDeadRef.current || !isSocketReadyRef.current) {
        isSettledRef.current = true;
        void runBatchTranscription("fallback");
        return;
      }
      send({ type: "stop" });
      finalizeTimeoutRef.current = setTimeout(
        () => failOver("no final transcript within 10s"),
        FINALIZE_TIMEOUT_MS,
      );
    })();
  }, [applyStatus, failOver, runBatchTranscription, send]);

  const cancel = useCallback(() => {
    isDiscardedRef.current = true;
    isSettledRef.current = true;
    send({ type: "cancel" });
    reset();
  }, [reset, send]);

  /**
   * Open the realtime channel and wire it up.
   *
   * Failure at any point is not fatal: capture is already running and the
   * buffered audio is transcribed through the batch endpoint on stop.
   */
  const connectRealtime = useCallback(() => {
    const socket = new WebSocket(getSocketUrl());
    socket.binaryType = "arraybuffer";
    socketRef.current = socket;

    // A socket stuck in CONNECTING would otherwise strand the session; drop to
    // the fallback while capture keeps running.
    clearConnectTimeout();
    connectTimeoutRef.current = setTimeout(() => {
      connectTimeoutRef.current = null;
      if (!isSocketReadyRef.current) {
        failOver("socket not ready within 8s");
      }
    }, CONNECT_TIMEOUT_MS);

    socket.onopen = () => {
      send({
        type: "auth",
        token: getBearerToken(),
        teamId:
          localStorage.getItem(LOCALSTORAGE_TEAM_SELECTED_KEY) || undefined,
      });
    };
    socket.onmessage = (event) => {
      try {
        handleServerMessage(
          JSON.parse(event.data as string) as TranscriptionServerMessage,
        );
      } catch {
        // Ignore frames we cannot parse; the session stays usable.
      }
    };
    socket.onerror = () => {
      clearConnectTimeout();
      // The browser never exposes why a WebSocket handshake failed; the most
      // common causes are a blocked mixed-content `ws://` on an HTTPS page and
      // an API host the device cannot reach.
      failOver("socket error — check the URL is reachable and not mixed-content");
    };
    socket.onclose = (event) => {
      clearConnectTimeout();
      // A close before the transcript arrived means the realtime path failed.
      failOver(`socket closed (code ${event.code})`);
    };
  }, [clearConnectTimeout, failOver, handleServerMessage, send]);

  const start = useCallback(async () => {
    if (statusRef.current !== "idle") {
      return;
    }

    isSettledRef.current = false;
    isDiscardedRef.current = false;
    isSocketReadyRef.current = false;
    isRealtimeDeadRef.current = false;
    isConnectionLossReportedRef.current = false;
    transcriptRef.current = "";
    audioChunksRef.current = [];
    applyStatus("starting");

    let recorder: PcmRecorder;
    try {
      recorder = await startPcmRecorder({
        sampleRate: TRANSCRIPTION_SAMPLE_RATE,
        onAudio: (chunk) => {
          // Retained even while streaming, so a socket that dies mid-sentence
          // can still be recovered through the batch fallback.
          audioChunksRef.current.push(chunk);
          const socket = socketRef.current;
          if (isSocketReadyRef.current && socket?.readyState === WebSocket.OPEN) {
            socket.send(chunk);
          }
        },
      });
    } catch (error) {
      applyStatus("idle");
      if (error instanceof MicrophoneError && error.reason === "permission-denied") {
        optionsRef.current.onPermissionDenied?.();
      } else {
        optionsRef.current.onError?.();
      }
      return;
    }

    recorderRef.current = recorder;
    setStream(recorder.stream);
    applyStatus("listening");

    if (isOffline()) {
      // No route out: opening a socket would only stall for the connect
      // timeout. Record now, transcribe when the network is back.
      failOver("device offline");
      reportConnectionLost();
    } else {
      connectRealtime();
    }

    durationTimeoutRef.current = setTimeout(stop, TRANSCRIPTION_MAX_DURATION_MS);
  }, [applyStatus, connectRealtime, failOver, reportConnectionLost, stop]);

  // Dictation involves no touching, so without this the device dims and locks
  // on its idle timer and the session dies mid-sentence.
  useWakeLock(status === "starting" || status === "listening");

  // Mobile browsers suspend or evict a backgrounded tab, which silently kills
  // capture. Close the session on the way out so the transcript is preserved.
  useEffect(() => {
    if (status !== "listening") {
      return;
    }
    const handleHide = () => {
      if (document.visibilityState === "hidden") {
        stop();
      }
    };
    document.addEventListener("visibilitychange", handleHide);
    window.addEventListener("pagehide", stop);
    return () => {
      document.removeEventListener("visibilitychange", handleHide);
      window.removeEventListener("pagehide", stop);
    };
  }, [status, stop]);

  // A connection that drops mid-sentence does not always close the socket: on a
  // Wi-Fi handover the frame is simply never delivered and the session sits
  // there looking alive while nothing reaches the model. The browser's own
  // offline signal is the earliest notice available, and it lets the UI say the
  // recording is still running instead of freezing on a half-sentence.
  useEffect(() => {
    if (status !== "listening") {
      return;
    }
    const handleOffline = () => {
      failOver("device went offline");
      reportConnectionLost();
    };
    window.addEventListener("offline", handleOffline);
    return () => window.removeEventListener("offline", handleOffline);
  }, [failOver, reportConnectionLost, status]);

  // The recording lives in this tab and nowhere else, so a reload during
  // capture or while the upload waits on the network destroys it. Nothing else
  // can recover it, which is exactly what `beforeunload` is for.
  useEffect(() => {
    if (status === "idle") {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [status]);

  // Release the microphone and socket if the component unmounts mid-session.
  // Marking the session discarded stops a pending upload from retrying against
  // a consumer that no longer exists.
  useEffect(
    () => () => {
      isDiscardedRef.current = true;
      teardown();
    },
    [teardown],
  );

  return {
    status,
    isRecording: status === "starting" || status === "listening",
    isFinalizing: status === "finalizing",
    stream,
    isLiveTranscript,
    isAwaitingConnection,
    start,
    stop,
    cancel,
  };
};
